'use client'
import { useQuery } from '@apollo/client/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { CSSProperties, ReactNode, createContext, useContext, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { colorBlender, getContrastRatio, isHexCode } from '@syconium/little-miss-figgy'

import { gql } from '../../__generated__/graphql/catalog'
import { getProductHandleFromPath } from '../../lib/utils/product-handle'

type AccentColor = NonNullable<CSSProperties['color']>
type HexCodes = Array<AccentColor>
type AccentColorContextValue = {
  hexCodes: HexCodes | undefined | null
}

const defaultAccentColorContextValue: AccentColorContextValue = {
  hexCodes: [],
}

const AccentColorContext = createContext<AccentColorContextValue>(defaultAccentColorContextValue)

const useAccentColorContainer = (): AccentColorContextValue => {
  // Normally, I would like to just pass this from the PDP route from the page itself. But since this hook gets called in the promo bar,
  // we need to have the same context all the way from the root of the app.
  // So instead, I'm going to pull the product handle and the color from the url path here.
  const pathname = usePathname()
  const productHandleFromPath = getProductHandleFromPath(pathname)
  const searchParams = useSearchParams()
  const colorFromSearchParams = searchParams?.get('color')
  const shouldFetchHexCodes = !!productHandleFromPath && !!colorFromSearchParams

  const { data } = useQuery(
    gql(`
      query GetAccentColorDirective($handle: String!) {
        siteFixtures(handle: $handle) {
          id
          enablePDPAccentColor
        }
      }
  `),
    {
      variables: {
        handle: 'default',
      },
      skip: !shouldFetchHexCodes,
    }
  )
  const {
    data: hexCodeData,
    previousData: previousHexCodeData,
    loading: hexCodeLoading,
  } = useQuery(
    gql(`
    query GetAccentColorHexCodes($handle: String!, $color: String!) {
      productCatalogStandardProduct(handle: $handle) {
        id
        variantGroup(color: $color) {
          id
          color {
            hexCodes
          }
        }
      }
    }
  `),
    {
      variables: {
        handle: productHandleFromPath ?? '',
        color: colorFromSearchParams ?? '',
      },
      skip: !shouldFetchHexCodes,
    }
  )

  const accentColorEnabled = data?.siteFixtures.enablePDPAccentColor

  const hexCodes = hexCodeLoading
    ? previousHexCodeData?.productCatalogStandardProduct.variantGroup.color?.hexCodes ?? []
    : hexCodeData?.productCatalogStandardProduct.variantGroup.color?.hexCodes ?? []

  return {
    hexCodes: accentColorEnabled && shouldFetchHexCodes ? hexCodes : [],
  }
}

export const AccentColorProvider = ({ children }: { children: ReactNode }) => {
  const value = useAccentColorContainer()

  return <AccentColorContext.Provider value={value}>{children}</AccentColorContext.Provider>
}

export const useAccentColor = (): {
  accentColor: AccentColor | undefined
} => {
  const theme = useTheme()
  const { hexCodes: hexCodesContext } = useContext(AccentColorContext)

  const accentColor = useMemo<AccentColor | undefined>(() => {
    const scopedThemeBackgroundColor = theme.color.fill.background
    const hexCodes = hexCodesContext ?? []
    const hexCode = hexCodes
      .concat() // Make mutable then sort to start with the color closest to our desired contrast ratio. It is the least likely to need altering.
      .filter(hexCode => isHexCode(hexCode))
      .sort((thisHexCode, thatHexCode) => {
        const thisContrast = getContrastRatio(scopedThemeBackgroundColor, thisHexCode)
        const thatContrast = getContrastRatio(scopedThemeBackgroundColor, thatHexCode)
        return thatContrast - thisContrast
      })
      .slice(0, 1) // Only process the most likely to succeed color.
      .map(hexCode => {
        let contrast = getContrastRatio(scopedThemeBackgroundColor, hexCode)
        // If we pass 3:1, just use the color.
        if (contrast >= 3) {
          return hexCode
        } else {
          // The max we are OK with lightening/darkening to make this work is 66%. Otherwise the color becomes too hard to recognize.
          const lightnessChangeSteps = [
            0.11, -0.11, 0.22, -0.22, 0.33, -0.33, 0.44, -0.44, 0.55, -0.55, 0.66, -0.66,
          ]
          let acceptableHexResult: string | undefined = undefined
          lightnessChangeSteps.forEach(step => {
            if (!acceptableHexResult) {
              const alteredHex = colorBlender(step, hexCode, undefined, undefined) ?? hexCode
              const alteredContrast = getContrastRatio(scopedThemeBackgroundColor, alteredHex)
              if (alteredContrast >= 3) {
                acceptableHexResult = alteredHex
              }
            }
          })
          return acceptableHexResult
        }
      })[0]

    if (hexCode) {
      return hexCode
    } else {
      return undefined
    }
  }, [hexCodesContext, theme.color.fill.background])

  return {
    accentColor,
  }
}
